import { PayloadAction } from '@reduxjs/toolkit';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Editor, Extension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { enqueueSnackbar } from 'notistack';
import React, {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import {
  getConversation,
  getConversations,
  getFolderAgnosticConversations,
  getMessages,
  getStreamingResponse,
  getStreamingResponseTemporary,
  postConversation,
  postLongRunningMessage,
  postMessage,
} from '../../redux/actions/conversationActions';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import {
  selectConversations,
  setCurrentConversation,
  setCurrentMessages,
  setIsLoading,
  setIsScrolling,
} from '../../redux/reducers/conversationReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { setShowConversationsOnly } from '../../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../../redux/store';
import { getNotes } from '../../services/dealGPT';
import { Conversation, Message, Metadata } from '../../Types/conversation';
import { ProjectScopeAreaType } from '../../Types/dealGPT';
import { AppEnum, IndexType, MessageFrom, ToolName } from '../../Types/enums';
import { CustomFile } from '../../Types/file';
import { LlmEnum, llms } from '../../Types/llm';
import { Prompt } from '../../Types/prompt';
import { ToolConfig } from '../../util/tools';
import { AnalyticsEvent, useAnalytics } from './AnalyticsProvider';
import { useAuth } from './AuthProvider';
import { useNotifications } from './NotificationsProvider';

interface HandleSubmitParams {
  conversationId?: string;
  input?: string;
  isPrivate?: boolean;
  isDrawerChat?: boolean;
  assistantId?: string;
  vectorStoreId?: string;
  redirectOnProcess?: boolean;
  isTemporaryChat?: boolean;
  toolsOverride?: ToolName[];
  summaryFileIds?: string[];
}

interface ChatContextType {
  inputRef: MutableRefObject<HTMLInputElement | undefined>;
  getChatPrompt: () => string;
  isStreaming: boolean;
  showErrorMessage: string;
  setEditorPrompt: (prompt: Prompt | undefined) => void;
  tools: ToolName[];
  setTools: React.Dispatch<React.SetStateAction<ToolName[]>>;
  uploadedFiles: CustomFile[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<CustomFile[]>>;
  showNormalTextField: boolean;
  setShowNormalTextField: React.Dispatch<React.SetStateAction<boolean>>;
  editor: Editor | null;
  submitMessage: (
    message: string,
    files: CustomFile[],
    conversationId: string,
    prompt?: Prompt,
    metadata?: Metadata,
    isDrawerChat?: boolean
  ) => void;
  replaceCurrentMessage: (updatedMessage: Message) => void;
  handleSubmit: (params: HandleSubmitParams) => Promise<void>;
  selectedScopes: ProjectScopeAreaType[];
  setSelectedScopes: React.Dispatch<React.SetStateAction<ProjectScopeAreaType[]>>;
  shouldCancelRef: MutableRefObject<boolean>;
  temporaryShouldCancelRef: MutableRefObject<boolean>;
  isNavigatingToNewConversationRef: MutableRefObject<boolean>;
  drawerConversationIdRef: React.MutableRefObject<string | null>;
  temporaryMessages: Message[];
  setTemporaryMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  clearChatState: () => void;
}

export const ChatContext = createContext<ChatContextType | null>(null);

export const ChatProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const { currentUser, refetchUser } = useAuth();
  const { conversationId: conversationIdParam } = useParams();
  const { currentConversation, currentMessages } = useSelector(selectConversations);
  const { selectedApp, llm } = useSelector(selectAppStatus);
  const { currentFolder } = useSelector(selectFolders);
  const { refetchNotifications } = useNotifications();
  const inputRef = useRef<HTMLInputElement>();
  const currentMessagesRef = useRef(currentMessages);
  const shouldCancelRef = useRef(false);
  const temporaryShouldCancelRef = useRef(false);
  const isNavigatingToNewConversationRef = useRef(false);
  const [tools, setTools] = useState<ToolName[]>([]);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<CustomFile[]>([]);
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [showNormalTextField, setShowNormalTextField] = useState(true);
  const [selectedScopes, setSelectedScopes] = useState<ProjectScopeAreaType[]>([]);
  const [temporaryMessages, setTemporaryMessages] = useState<Message[]>([]);
  const temporaryMessagesRef = useRef(temporaryMessages);
  const promptRef = useRef<Prompt | undefined>();
  const drawerConversationIdRef = useRef<string | null>(null);

  const EnterKeyExtension = Extension.create({
    name: 'enterKey',

    addKeyboardShortcuts() {
      return {
        'Mod-Enter': () => this.editor.commands.enter(),
        Enter: () => {
          // Prevent default Enter behavior
          return true;
        },
      };
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      EnterKeyExtension,
      Highlight.configure({ multicolor: true }),
    ],
    shouldRerenderOnTransaction: false,
  });

  /**
   * Sets the current editor prompt value and updates the chat prompt state.
   *
   * If a prompt is provided, its `prompt` property is set as the value of the input referenced by `inputRef`.
   * If no prompt is provided, the input value is cleared.
   * We save the Chat Prompt when user edit is required. In order to later use it to submit the conversation and associate the prompt with the conversation.
   *
   * @param prompt - The `Prompt` object to set in the editor and chat state, or `undefined` to clear.
   */
  const setEditorPrompt = useCallback(
    (prompt: Prompt | undefined) => {
      if (inputRef.current) {
        inputRef.current.value = prompt?.prompt ?? '';
      }
      if (editor) {
        if (prompt?.prompt) {
          editor.commands.setContent(prompt.prompt);
        } else {
          editor.commands.clearContent();
        }
      }
      promptRef.current = prompt;
    },
    [inputRef, editor]
  );

  const clearChatState = useCallback(() => {
    dispatch(setCurrentConversation(undefined));
    setTools([]);
    setUploadedFiles([]);
    setEditorPrompt(undefined);
    setShowErrorMessage('');
    setSelectedScopes([]);
    setTemporaryMessages([]);
  }, [dispatch, setUploadedFiles, setEditorPrompt]);

  const getChatPrompt = useCallback(() => {
    if (inputRef.current) {
      return inputRef.current.value;
    }
    if (editor) {
      return editor.getText().replace(/\s+([,.!?;:])/g, '$1');
    }
    return '';
  }, [inputRef, editor]);

  const getAllScopeAreaNotes = useCallback(
    async (projectId: string, scopes: ProjectScopeAreaType[]) => {
      try {
        let siteNotesToAppend =
          'You are a M&A consultant, I will provide site visit notes for different scope areas that you will use to answer the question. \n\n';

        //Fetching Notes for each selected scope Area of the current project.
        await Promise.all(
          scopes.map(async (selectedScope) => {
            const notes = await getNotes(projectId, selectedScope.scope);
            if (notes && notes.notes !== '') {
              siteNotesToAppend += `Site visit notes for ${selectedScope.scope}:\n${notes.notes}\n\n`;
            }
          })
        );

        siteNotesToAppend += 'Answer this question:\n';

        return siteNotesToAppend;
      } catch (error) {
        enqueueSnackbar('Failed to fetch scope area notes. Please try again later.', {
          variant: 'error',
        });
      }
    },
    []
  );

  /**
   * Generic stream processor that handles common streaming logic for both temporary and regular messages.
   * Extracts chunk parsing, reasoning handling, and message updates into a reusable function.
   */
  const processStreamingResponse = useCallback(
    async (
      response: Response,
      config: {
        conversationId: string;
        messagesRef: React.MutableRefObject<Message[]>;
        cancelRef: React.MutableRefObject<boolean>;
        updateMessages: (updater: (messages: Message[]) => Message[]) => void;
        onConversationIdExtracted?: (convoId: string) => void;
        onComplete?: (convoId: string) => void;
        handleTokens?: boolean;
      }
    ) => {
      if (!response || !response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const initBotMessage = {
        conversationId: config.conversationId,
        from: MessageFrom.AI,
        message: '',
      } as Message;

      config.updateMessages((prev) => [...prev, initBotMessage]);
      setUploadedFiles([]);

      let done = false;
      let value: Uint8Array | undefined;
      let message = '';
      let readingReasoning = false;
      let reasoningSummary = '';
      let convoId = '';

      do {
        ({ done, value } = await reader.read());

        if (config.cancelRef.current) {
          await reader.cancel();
          config.cancelRef.current = false;
          dispatch(setIsLoading(false));
          setIsStreaming(false);
          if (config.onComplete) {
            config.onComplete(convoId);
          }
          return;
        }

        if (done) break;

        let chunk = decoder.decode(value, { stream: true });

        // Handle INITIAL CONVERSATION ID
        const convoIdIndex = chunk.indexOf('INITIAL CONVERSATION ID:');
        if (convoIdIndex >= 0) {
          if (config.conversationId === 'privateStreaming') {
            // For temporary chat, skip the "private" text
            chunk = chunk.slice(convoIdIndex + 25 + 'private'.length);
            dispatch(setIsScrolling(false));
          } else {
            // For regular chat, extract UUID
            const uuidLength = 36;
            convoId = chunk.slice(convoIdIndex + 25, convoIdIndex + 25 + uuidLength);
            chunk = chunk.slice(convoIdIndex + 25 + uuidLength);
            if (config.onConversationIdExtracted) {
              config.onConversationIdExtracted(convoId);
            }
          }
        }

        // Handle STARTING REASONING SUMMARY
        const reasoningStartIndex = chunk.indexOf('STARTING REASONING SUMMARY');
        if (reasoningStartIndex >= 0) {
          chunk = chunk.slice(reasoningStartIndex + 26);
          readingReasoning = true;
        }

        // Handle ENDING REASONING SUMMARY
        const reasoningEndIndex = chunk.indexOf('ENDING REASONING SUMMARY');
        if (reasoningEndIndex >= 0 && readingReasoning) {
          chunk = chunk.slice(reasoningEndIndex + 24);
          readingReasoning = false;
        }

        // Handle STARTING CONTENT
        const startingContentIndex = chunk.indexOf('STARTING CONTENT');
        if (startingContentIndex >= 0) {
          const remainderChunk = chunk.slice(0, startingContentIndex);
          if (remainderChunk) {
            reasoningSummary += remainderChunk;
            const newMessages = [...config.messagesRef.current];
            newMessages[newMessages.length - 1] = {
              ...newMessages[newMessages.length - 1],
              message,
              reasoningSummary,
            };
            config.updateMessages(() => newMessages);
          }
          chunk = chunk.slice(16);
        }

        // Handle AI TOKENS (for temporary chat only)
        if (config.handleTokens) {
          const aiTokenIndex = chunk.indexOf('AI TOKENS:');
          if (aiTokenIndex !== -1) {
            const restOfString = chunk.slice(aiTokenIndex + 'AI TOKENS:'.length);
            const aiTokens = parseInt(restOfString);
            config.updateMessages((messages) => {
              const newMessages = [...messages];
              newMessages[newMessages.length - 1] = {
                ...newMessages[newMessages.length - 1],
                tokens: aiTokens,
              };
              return newMessages;
            });
          }
        }

        // Handle NEW CONVERSATION ID (completion marker)
        const doneIndex = chunk.indexOf('NEW CONVERSATION ID:');
        if (doneIndex >= 0) {
          if (config.conversationId !== 'privateStreaming') {
            convoId = chunk.slice(doneIndex + 21);
            dispatch(setCurrentConversation(undefined));
            isNavigatingToNewConversationRef.current = true;
          }
          done = true;
          if (config.onComplete) {
            config.onComplete(convoId);
          }
        } else {
          // Accumulate message or reasoning content
          if (readingReasoning) {
            reasoningSummary += chunk;
          } else {
            message += chunk;
          }

          const newMessages = [...config.messagesRef.current];
          newMessages[newMessages.length - 1] = {
            ...newMessages[newMessages.length - 1],
            message,
            reasoningSummary,
            ...(config.handleTokens ? { tokens: 0 } : {}),
          };
          config.updateMessages(() => newMessages);
        }
      } while (!done);

      dispatch(setIsLoading(false));
      setIsStreaming(false);
    },
    [dispatch]
  );

  const handleTemporaryStreamingResponse = useCallback(
    async (response: Response) => {
      await processStreamingResponse(response, {
        conversationId: 'privateStreaming',
        messagesRef: temporaryMessagesRef,
        cancelRef: temporaryShouldCancelRef,
        updateMessages: setTemporaryMessages,
        handleTokens: true,
      });
    },
    [processStreamingResponse]
  );

  const handleStreamingResponse = useCallback(
    async (response: Response, isDrawerChat?: boolean) => {
      const navigateToConvo = (convoId: string) => {
        setIsStreaming(false);
        dispatch(setIsLoading(false));
        if (convoId) {
          dispatch(getConversations());
          dispatch(setShowConversationsOnly(true));
          dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));

          if (isDrawerChat) {
            drawerConversationIdRef.current = convoId;
            dispatch(getConversation(convoId)).then((action) => {
              if (getConversation.fulfilled.match(action)) {
                const conversation = action.payload;
                dispatch(setCurrentConversation(conversation));
              }
            });

            dispatch(getMessages(convoId)).then((action) => {
              if (getMessages.fulfilled.match(action)) {
                const messages = action.payload;
                dispatch(setCurrentMessages(messages));
              }
            });
          } else {
            navigate(`/folder/${currentFolder?.id ?? myFolder.id}/${convoId}`);
          }
        }
      };

      await processStreamingResponse(response, {
        conversationId: currentConversation?.id ?? '',
        messagesRef: currentMessagesRef,
        cancelRef: shouldCancelRef,
        updateMessages: (updater) => {
          const newMessages = updater(currentMessagesRef.current);
          dispatch(setCurrentMessages(newMessages));
        },
        onComplete: navigateToConvo,
      });
    },
    [processStreamingResponse, currentConversation?.id, navigate, dispatch, currentFolder?.id]
  );

  const submitMessage = useCallback(
    (
      message: string,
      files: CustomFile[],
      conversationId: string,
      prompt?: Prompt,
      metadata?: Metadata,
      isDrawerChat?: boolean
    ) => {
      let effectiveConversationId = conversationId;

      // If in drawer chat and we already have a conversation saved, reuse it
      if (isDrawerChat && drawerConversationIdRef.current) {
        effectiveConversationId = drawerConversationIdRef.current;
      }

      const lastMessage = currentMessagesRef.current[currentMessagesRef.current.length - 1];
      const isApprovalResume =
        message.trim().length === 0 &&
        files.length === 0 &&
        !prompt &&
        !metadata &&
        lastMessage?.conversationId === effectiveConversationId &&
        lastMessage?.from === MessageFrom.AI &&
        Boolean(lastMessage?.mcpApprovalRequests?.some((request) => request.approved === true));

      // This function is here so that we can set the currentMessages if needed, but we can avoid it for an autosend prompt
      // we need to use the dispatch().then() pattern if we're doing both, but there's not an easy way to conditionalize that without a lot of duplication
      let messageId = '';
      const oldMessages = [...currentMessagesRef.current]; // Store previous state

      const actuallySubmitMessage = () => {
        if (selectedApp !== AppEnum.WM_EMPLOYEE_HANDBOOK) {
          dispatch(setIsScrolling(false));
          let llmToPass = llm;

          if (currentFolder?.project) {
            if (currentFolder.project.indexToUse === IndexType.OPENAI) {
              llmToPass = LlmEnum.OPENAI_RAG;
            } else if (currentFolder.project.indexToUse === IndexType.GRAPH_RAG) {
              llmToPass = LlmEnum.GRAPH_RAG;
            }
          }

          dispatch(
            getStreamingResponse({
              message: {
                userId: currentUser?.id ?? '',
                conversationId: effectiveConversationId,
                message,
                promptId: prompt?.id,
                files,
                tools,
              },
              userId: currentUser?.id ?? '',
              llm: llmToPass,
              indexId: currentFolder?.indexIds?.[0],
              selectedApp,
            })
          ).then((action) => {
            const rejected = action.type.endsWith('/rejected');
            setIsStreaming(!rejected);

            if (rejected) {
              // Revert to previous messages state if rejected
              currentMessagesRef.current = oldMessages;
              dispatch(setCurrentMessages(oldMessages));
              return;
            }

            handleStreamingResponse(action.payload as Response, isDrawerChat);
            if (isDrawerChat) {
              dispatch(getMessages(effectiveConversationId));
            }
          });
        } else {
          dispatch(
            postMessage({
              message: {
                userId: currentUser?.id ?? '',
                conversationId: effectiveConversationId,
                message,
                files,
              },
              llm: currentFolder?.project ? LlmEnum.OPENAI_RAG : llm,
              indexId: currentFolder?.indexIds?.[0],
            })
          ).then((action: PayloadAction<Message | unknown>) => {
            const message = action.payload as Message;
            messageId = message.id;

            if (!conversationIdParam) {
              dispatch(setShowConversationsOnly(true));
              dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
              dispatch(getConversations());
            }

            dispatch(getMessages(effectiveConversationId)).then(
              (action: PayloadAction<Message[] | unknown>) => {
                dispatch(setCurrentMessages(action.payload as Message[]));
                dispatch(setIsLoading(false));
                if (!isDrawerChat)
                  navigate(
                    `/folder/${currentFolder?.id ?? myFolder.id}/${effectiveConversationId}`
                  );
              }
            );
          });

          if (!metadata) {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_MESSAGE,
              {
                id: messageId,
              },
              {
                messageId,
                conversationId: effectiveConversationId,
                attachedFileIds: files.map((file) => file.id),
                llmUsed: llms[llm].deploymentId,
              }
            );
          }
        }
      };

      if ((prompt && !prompt.userEditRequired && !effectiveConversationId) || isApprovalResume) {
        actuallySubmitMessage();
      } else {
        const newMessages = [
          ...currentMessagesRef.current,
          {
            userId: currentUser?.id ?? '',
            conversationId: effectiveConversationId,
            message,
            from: MessageFrom.USER,
            createdAt: '',
            id: '',
            deleted: false,
            tokens: 0,
            files,
            ...(metadata ? { metadata } : {}),
          },
        ];
        currentMessagesRef.current = newMessages;
        dispatch(setCurrentMessages(newMessages));
        actuallySubmitMessage();
      }
    },
    [
      dispatch,
      currentUser?.id,
      llm,
      handleStreamingResponse,
      navigate,
      analytics,
      conversationIdParam,
      currentFolder,
      selectedApp,
      tools,
    ]
  );

  const replaceCurrentMessage = useCallback(
    (updatedMessage: Message) => {
      const nextMessages = currentMessagesRef.current.map((message) =>
        message.id === updatedMessage.id ? updatedMessage : message
      );

      currentMessagesRef.current = nextMessages;
      dispatch(setCurrentMessages(nextMessages));
    },
    [dispatch]
  );

  const createNewConversation = useCallback(
    (
      message: string,
      isPrivate: boolean,
      tempUploadedFiles: CustomFile[],
      newConversationTitle: string,
      prompt?: Prompt,
      metadata?: Metadata,
      isDrawerChat?: boolean,
      redirectOnProcess?: boolean,
      vectorStoreId?: string,
      summaryFileIds?: string[]
    ) => {
      return dispatch(
        postConversation({
          title: newConversationTitle,
          tools: [...tools, ...(prompt?.tools ?? [])],
          appName: selectedApp,
          projectId: currentFolder?.projectId ?? undefined,
          folderId: currentFolder?.id === myFolder.id ? undefined : currentFolder?.id,
          isPrivate,
          vectorStoreId,
          summaryFileIds,
        })
      ).then((action: PayloadAction<Conversation | unknown>) => {
        const newConversation = action.payload as Conversation;
        if (isDrawerChat) {
          drawerConversationIdRef.current = newConversation.id;
          currentMessagesRef.current = [];

          if (redirectOnProcess) {
            dispatch(setShowConversationsOnly(true));
            dispatch(getConversations());
            dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
            dispatch(setCurrentConversation(newConversation));

            navigate(`/folder/${currentFolder?.id ?? myFolder.id}/${newConversation.id}`);
            return;
          }
        }
        submitMessage(
          message,
          tempUploadedFiles,
          newConversation.id,
          prompt,
          metadata,
          isDrawerChat
        );
      });
    },
    [
      dispatch,
      tools,
      selectedApp,
      submitMessage,
      currentFolder?.id,
      currentFolder?.projectId,
      drawerConversationIdRef,
      navigate,
    ]
  );

  const handleSubmit = useCallback(
    async ({
      conversationId,
      input,
      isPrivate,
      isDrawerChat,
      vectorStoreId,
      redirectOnProcess,
      isTemporaryChat,
      toolsOverride,
      summaryFileIds,
    }: HandleSubmitParams) => {
      const prompt = promptRef.current;
      const editorContentText = editor?.getText();
      const toolsToUse = toolsOverride || tools;
      let siteNotesToAppend = '';
      let metadata = undefined;

      if (isDrawerChat && drawerConversationIdRef.current) {
        conversationId = drawerConversationIdRef.current;
      }

      if (selectedApp === AppEnum.DEAL_GPT) {
        analytics.event(
          AnalyticsEvent.WM_IA_CHAT_SEND_MESSAGE,
          {
            id: conversationId,
            value: `ProjectId=${
              currentFolder?.projectId
            },Scopes=${selectedScopes.toString()},Message=${input}`,
          },
          {
            projectId: currentFolder?.projectId,
            conversationId,
            selectedScopes: selectedScopes.map((scope) => scope.scope),
          }
        );

        //Fetching all user selected scope area site visit notes to append at the beggining of message.
        if (selectedScopes.length > 0) {
          siteNotesToAppend =
            (await getAllScopeAreaNotes(currentFolder?.projectId as string, selectedScopes)) ?? '';
          const scopes = selectedScopes.map((scopeArea) => {
            return scopeArea.scope;
          });

          const metadataObject: Metadata = {
            properties: [
              {
                name: 'SiteNotes',
                value: scopes,
              },
            ],
          };

          metadata = metadataObject;
        }
      }

      if (input && conversationId && !isTemporaryChat) {
        dispatch(setIsLoading(true));

        //site notes will be an empty string if it's a Nigel message.
        const message = `${siteNotesToAppend}${input}`;
        submitMessage(message, uploadedFiles, conversationId, prompt, metadata, isDrawerChat);
      } else if (inputRef.current || editorContentText) {
        const text = getChatPrompt();

        if (text === '' && uploadedFiles.length === 0) {
          setShowErrorMessage('Please enter your message!');
          setTimeout(() => {
            setShowErrorMessage('');
          }, 5000);
          return;
        }

        dispatch(setIsLoading(true));

        const message = `${siteNotesToAppend} ${text}`;
        let tempUploadedFiles: CustomFile[] = uploadedFiles.filter((file) => !file.isStarterFile);
        tempUploadedFiles = [...(prompt?.files ?? []), ...tempUploadedFiles];

        setUploadedFiles([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        } else {
          editor?.commands.setContent('');
        }

        // Handle temporary chat submission - don't create conversation or navigate
        if (isTemporaryChat) {
          // Filter out DEEP_RESEARCH from tools for temporary chat (this could change in the future)
          const TEMPORARY_CHAT_EXCLUDED_TOOLS = [ToolName.DEEP_RESEARCH];
          const filteredTools = tools.filter(
            (tool) => !TEMPORARY_CHAT_EXCLUDED_TOOLS.includes(tool)
          );

          // Add user message to temporary messages
          const temporaryMessage: Message = {
            userId: '',
            conversationId: 'privateStreaming',
            message,
            from: MessageFrom.USER,
            createdAt: '',
            id: '',
            deleted: false,
            tokens: 0,
            files: tempUploadedFiles,
            azureCitations: '',
            citations: [],
            tools: filteredTools,
          };

          setTemporaryMessages((prev) => [...prev, temporaryMessage]);

          // Call the temporary streaming API
          dispatch(
            getStreamingResponseTemporary({
              message: { userId: '', message, files: tempUploadedFiles, tools: filteredTools },
              messageArray: temporaryMessagesRef.current,
              userId: currentUser?.id ?? '',
              llm,
            })
          ).then((action: PayloadAction<Response | unknown>) => {
            const rejected = action.type.endsWith('/rejected');
            setIsStreaming(!rejected);

            if (rejected) {
              // Revert to previous messages state if rejected
              setTemporaryMessages((prevMessages) => prevMessages.slice(0, -1));
              return;
            }

            handleTemporaryStreamingResponse(action.payload as Response);
          });

          if (inputRef.current) {
            inputRef.current.focus();
          }
          return;
        }

        if (toolsToUse.includes(ToolName.DEEP_RESEARCH)) {
          analytics.event(AnalyticsEvent.WM_NIGEL_DEEP_RESEARCH_STARTED, {
            value: currentUser?.id,
          });
          await dispatch(
            postLongRunningMessage({
              message: {
                conversationId,
                tools: toolsToUse.filter((tool) => ToolConfig[tool].deepResearchSupport),
                appName: selectedApp,
                message,
                files: tempUploadedFiles,
                userId: currentUser?.id ?? '',
                folderId: currentFolder?.id === myFolder.id ? undefined : currentFolder?.id,
                llm,
              },
            })
          );

          refetchUser(currentUser?.id || '');
          dispatch(setIsLoading(false));
          clearChatState();
          refetchNotifications();

          if (!isDrawerChat) {
            navigate(`/folder/${currentFolder?.id ?? myFolder.id}`);
          }

          return;
        }

        if (!conversationId) {
          let newConversationTitle;
          if (prompt?.title) {
            newConversationTitle = prompt.title;
          } else if (tempUploadedFiles.length > 0) {
            const fileName = tempUploadedFiles[0].fileName;
            if (fileName.length > 20) {
              const extension = fileName.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2);
              newConversationTitle = `${fileName.substring(0, 20)}...${extension}`;
            } else {
              newConversationTitle = fileName;
            }
          } else {
            newConversationTitle = message;
          }

          if (location.pathname !== `/folder/${currentFolder?.id ?? myFolder.id}/new-chat`) {
            if (!isDrawerChat) {
              navigate(`/folder/${currentFolder?.id ?? myFolder.id}/new-chat`);
            }

            // Adding a small delay to ensure the route for IA chat is fully loaded before creating a new conversation.
            await new Promise((resolve) => setTimeout(resolve, 3000));
          }

          await createNewConversation(
            message,
            isPrivate ?? false,
            tempUploadedFiles,
            newConversationTitle,
            prompt,
            metadata,
            isDrawerChat,
            redirectOnProcess,
            vectorStoreId,
            summaryFileIds
          );
        } else {
          submitMessage(message, tempUploadedFiles, conversationId, prompt, metadata, isDrawerChat);
        }

        //Clean up after submitting the message

        if (selectedApp === AppEnum.DEAL_GPT) {
          setSelectedScopes([]);
        }

        if (inputRef.current) {
          inputRef.current.focus();
        }

        setShowNormalTextField(true);
        promptRef.current = undefined;
      }
    },
    [
      selectedApp,
      analytics,
      editor,
      selectedScopes,
      uploadedFiles,
      tools,
      currentFolder?.id,
      currentFolder?.projectId,
      navigate,
      dispatch,
      createNewConversation,
      getChatPrompt,
      submitMessage,
      getAllScopeAreaNotes,
      currentUser,
      refetchUser,
      refetchNotifications,
      handleTemporaryStreamingResponse,
      llm,
      clearChatState,
    ]
  );

  // updates the currentMessagesRef when currentMessages updates
  useEffect(() => {
    if (!isStreaming) {
      currentMessagesRef.current = currentMessages;
    }
  }, [currentMessages, isStreaming]);

  // updates the temporaryMessagesRef when temporaryMessages updates
  useEffect(() => {
    temporaryMessagesRef.current = temporaryMessages;
  }, [temporaryMessages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const value = useMemo(
    () => ({
      getChatPrompt,
      inputRef,
      isStreaming,
      showErrorMessage,
      setEditorPrompt,
      tools,
      setTools,
      uploadedFiles,
      setUploadedFiles,
      showNormalTextField,
      setShowNormalTextField,
      editor,
      submitMessage,
      replaceCurrentMessage,
      handleSubmit,
      selectedScopes,
      setSelectedScopes,
      shouldCancelRef,
      temporaryShouldCancelRef,
      isNavigatingToNewConversationRef,
      drawerConversationIdRef,
      temporaryMessages,
      setTemporaryMessages,
      clearChatState,
    }),
    [
      getChatPrompt,
      inputRef,
      isStreaming,
      showErrorMessage,
      setEditorPrompt,
      tools,
      setTools,
      uploadedFiles,
      setUploadedFiles,
      showNormalTextField,
      setShowNormalTextField,
      editor,
      submitMessage,
      replaceCurrentMessage,
      handleSubmit,
      selectedScopes,
      setSelectedScopes,
      shouldCancelRef,
      temporaryShouldCancelRef,
      isNavigatingToNewConversationRef,
      drawerConversationIdRef,
      temporaryMessages,
      setTemporaryMessages,
      clearChatState,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextType => {
  const chatContext = useContext(ChatContext);

  if (!chatContext) {
    throw new Error('useChat must be used within a ChatProvider');
  }

  return chatContext;
};
